import type { FC } from "react";
import { useMemo, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";

import { StoreLocatorCardList } from "@locators/components/shared/components/StoreLocatorCardList";
import { StoreLocatorMap } from "@locators/components/shared/components/StoreLocatorMap";
import { StoreLocatorProviderWrapper } from "@locators/components/shared/components/StoreLocatorProviderWrapper";
import { StoreLocatorSearchBarV2 } from "@locators/components/shared/components/StoreLocatorSearchBarV2";
import { ViewSwitch } from "@locators/components/shared/components/ViewSwitch";
import { StoreLocatorContext } from "@locators/components/shared/context";
import { useCSSProperty, useHeight } from "@locators/components/shared/hooks";
import { useDisplayType } from "@locators/components/shared/hooks/useDisplayType";
import classNames from "classnames";

import { DrawerRoot } from "@jti/ui/components/Drawer";
import { useDetectTheme } from "@jti/ui/hooks/useDetectTheme";

import { EventsLocatorDateFilter } from "./components/EventsLocatorDateFilter";
import { getInitialEventsDataFromURL } from "./utils/convertEventsFilterData";

import { useEventsFiltersShare, useEventsLocator } from "./hooks";
import type { EventsLocatorProps } from "./types";

import styles from "@locators/components/shared/styles/LocatorLayout.module.scss";

export const EventsLocator: FC<EventsLocatorProps> = (props) => {
  const { isDarkTheme } = useDetectTheme();
  const resolvedTheme = isDarkTheme ? ("dark" as const) : ("light" as const);

  const initialData = useMemo(() => getInitialEventsDataFromURL(), []);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | undefined>(
    initialData.dateRange ?? props.selectedDateRange
  );

  const initialMapPosition = useMemo(() => {
    const position = { defaultLat: props.defaultLat, defaultLng: props.defaultLng, defaultZoom: props.defaultZoom };
    const { lat, lng, zoom } = initialData.mapPosition;

    if (typeof lat === "number" && typeof lng === "number" && typeof zoom === "number") {
      position.defaultLat = lat.toString();
      position.defaultLng = lng.toString();
      position.defaultZoom = zoom;
    }

    return position;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const eventsLocatorData = useEventsLocator({ ...props, ...initialMapPosition, selectedDateRange }, resolvedTheme);
  const { isMapActive } = eventsLocatorData;

  useEventsFiltersShare({
    mapPosition: eventsLocatorData.mapPosition,
    dateRange: selectedDateRange,
    isInEditor: props.isInEditor,
  });
  const { change, isList, isMap } = useDisplayType("map");
  const { ref: topBarRef, height } = useHeight<HTMLDivElement>();
  const storeRef = useCSSProperty("--storeLocatorTopBarHeight", isMapActive ? `-${height}px` : "unset", [
    height,
    isMapActive,
  ]);
  const mainRef = useRef<HTMLDivElement>(null);

  const handleApplyDateRange = (range: DateRange | undefined) => {
    setSelectedDateRange(range);
    props.onDateRangeChange?.(range);
  };

  return (
    <StoreLocatorProviderWrapper>
      <StoreLocatorContext.Provider value={eventsLocatorData}>
        <div
          className={classNames(styles.storeLocator, { [styles.storeLocatorFixedHeight]: isMap })}
          ref={storeRef}
          translate="no"
        >
          <div className={classNames(styles.top, { [styles.hide]: isMapActive && isMap })} ref={topBarRef}>
            <div className={classNames(styles.wrapper)}>
              <DrawerRoot>
                <StoreLocatorSearchBarV2
                  filtersSlot={
                    <EventsLocatorDateFilter
                      selected={selectedDateRange}
                      onApplyDateRange={handleApplyDateRange}
                      dateRangePickerDropdownAlignment="right"
                    />
                  }
                  isFilterSlotAutoWidth
                  isSearchInputFullWidth
                />
              </DrawerRoot>
            </div>
          </div>

          <div className={classNames(styles.main)} ref={mainRef}>
            <div className={classNames(styles.wrapper, styles.desktopHidden)}>
              <ViewSwitch
                className={classNames(styles.viewSwitch, {
                  [styles.viewSwitchOnMap]: isMap,
                  [styles.fullSize]: isMapActive && isMap,
                })}
                onChangeResultType={change}
              />
            </div>
            <div className={classNames(styles.wrapper, styles.grid)}>
              <div
                className={classNames({
                  [styles.mobileHidden]: !isList,
                })}
              >
                <StoreLocatorCardList isMapView={isMap} mapWrapperRef={mainRef} />
              </div>
              <StoreLocatorMap className={classNames(styles.map, { [styles.mobileHidden]: !isMap })} />
            </div>
          </div>
        </div>
      </StoreLocatorContext.Provider>
    </StoreLocatorProviderWrapper>
  );
};
